<template>
    <div v-if="props.imageElement" class="image-effects-properties-wrapper">
        <label>Effects</label>
        <div class="divider mb-3" />
        <div v-if="hasEffects" class="draggable-list-wrapper mb-3">
            <image-effects-properties-list
                :items="model.imageEffects"
                :disabled="isDisabled"
                @remove="removeImageEffect"
                @update="updateImageEffect"
                @reorder="handleReorder"
            />
        </div>
        <div
            v-if="showMixed"
            class="item-row px-3 py-2 d-flex align-items-center w-100 mixed-row"
            :class="{ pointer: !isDisabled }"
            @click="!isDisabled && openAddEffectModal()"
        >
            Mixed Effects <span>Replace</span>
        </div>
        <div v-if="!imageElement.imageToImage" class="d-flex justify-content-center">
            <c-button
                class="w-full"
                size="md"
                text="Add Image Effect"
                variant="light"
                :icon="['fa-solid', 'fa-sparkles']"
                :disabled="isDisabled"
                @click="openAddEffectModal"
            />
            <image-effects-properties-create @update="updateImageEffect" />
        </div>
        <div class="footer mt-4">
            <div class="divider mb-3" />
            <font-awesome-icon :icon="['fa-solid', 'fa-key']" />
            Connect your API keys in <a href="/account/integrations" target="_blank">Account Integrations</a>.
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, watch, reactive, ref, provide, nextTick } from 'vue';
import CButton from '@/components/shared/CButton';
import type { CanvasEx } from '@/typings/canvas';
import type { ActiveSelection, ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import { isTextToImageEffect, type ImageEffectType } from '@/typings/customilyElements/imageEffectType';
import ImageEffectsPropertiesList from '@/components/DesignStudio/ImageEffectsProperties/components/list/ImageEffectsPropertiesList.vue';
import ImageEffectsPropertiesCreate from '@/components/DesignStudio/ImageEffectsProperties/components/create/ImageEffectsPropertiesCreate.vue';
import store from '@/store';

interface Props {
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
    selectedGroup?: ActiveSelection<ImagePlaceholderElement>;
}

const props = defineProps<Props>();

const model = reactive({
    imageEffects: props.imageElement?.imageEffects || []
});

// Provide: id del efecto a abrir (último agregado)
const openEffectId = ref<string | null>(null);
provide('openEffectId', openEffectId);

// Provide: si el elemento actual tiene un efecto Text to Image, para deshabilitar esos presets en el picker
const hasTextToImageEffect = computed(() => model.imageEffects.some((effect) => isTextToImageEffect(effect.type)));
provide('hasTextToImageEffect', hasTextToImageEffect);

const isDisabled = computed(() => props.imageElement?.locked);
const hasEffects = computed(() => model.imageEffects && model.imageEffects.length > 0);
const imageEffect = computed(() => store.state.imageEffectsProperties?.imageEffect);
const showMixed = ref(!!props.imageElement?.containsImageEffects && !props.imageElement?.imageEffects);

const updateImageEffects = () => {
    if (!props.imageElement || isDisabled.value) return;
    const objectsToEdit = props.selectedGroup ? props.selectedGroup?.getObjects() : [props.imageElement];
    for (const object of objectsToEdit) {
        object.set('imageEffects', model.imageEffects);

        if (props.editMode) {
            object.setEditedProp('imageEffects');
        }
    }
    showMixed.value = false;
    props.canvas.requestRenderAll();
};

const openAddEffectModal = () => {
    store?.dispatch('imageEffectsProperties/openAddModal');
};

const removeImageEffect = (effect: ImageEffectType) => {
    if (!model.imageEffects || isDisabled.value) return;

    const effectId = effect.id;
    const index = model.imageEffects.findIndex((e) => e.id === effectId);

    if (index !== -1) {
        model.imageEffects.splice(index, 1);
        updateImageEffects();
    }
};

const updateImageEffect = async (effect: ImageEffectType, newData?: ImageEffectType | undefined) => {
    if (!model.imageEffects || isDisabled.value) return;

    const effectId = effect.id;
    await nextTick();
    const index = model.imageEffects.findIndex((e) => e.id === effectId);
    if (index !== -1) {
        model.imageEffects[index] = newData ? { ...effect, ...newData } : effect;
        model.imageEffects = [...model.imageEffects];
        updateImageEffects();
        store.dispatch('imageEffectsProperties/closeAddModal');
    }
};

const handleReorder = (newEffects: ImageEffectType[]) => {
    if (!model.imageEffects || isDisabled.value) return;

    model.imageEffects = newEffects;
    updateImageEffects();
};

watch(
    () => props.imageElement?.imageEffects,
    () => {
        if (props.imageElement?.imageEffects) {
            model.imageEffects = [...props.imageElement.imageEffects];
        } else {
            model.imageEffects = [];
        }
        showMixed.value = !!props.imageElement?.containsImageEffects && !props.imageElement?.imageEffects;
    },
    { deep: true }
);

watch(
    () => imageEffect.value,
    async (value) => {
        if (!value) return;

        if (isTextToImageEffect(value.type)) {
            model.imageEffects = model.imageEffects.filter((effect) => !isTextToImageEffect(effect.type));
            model.imageEffects.unshift(value);
        } else {
            model.imageEffects.push(value);
        }
    }
);
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.mixed-row {
    border: 1px solid var(--gray-light);
    border-radius: 0.25rem;
    margin-bottom: 0.5rem !important;

    span {
        color: #7258e0;
        margin-left: 0.5rem;
    }
    &.pointer {
        cursor: pointer;
        span:hover {
            text-decoration: underline;
        }
    }
}
.draggable-list-wrapper {
    border: 1px solid var(--gray-light);
    border-radius: 0.25rem;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 0.375rem;
        height: 0.375rem;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
        border: none;
        box-shadow: unset;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #e6e6e6;
    }
}
.empty-state {
    border: 1px solid var(--gray-light);
    border-radius: 0.25rem;
}

.w-full {
    width: 100%;
}

.divider {
    border-top: 1px solid var(--gray-light);
}
</style>
